import { apiRequest } from './client';

/** Этап работ. Процент не хранится — считается по completed. */
export interface ContourStage {
  id: string;
  themeId: string;
  name: string;
  description?: string | null;
  completed: boolean;
  order: number;
  status: ContourTaskStatus;
  priority: ContourTaskPriority;
  deadlineUtc?: string | null;
  assigneeId?: string | null;
  version: number;
}

export type ContourTaskStatus = 'Todo' | 'InProgress' | 'Blocked' | 'Done' | 'Cancelled';
export type ContourTaskPriority = 'Low' | 'Normal' | 'High' | 'Urgent';
export interface ContourTaskMetadata {
  status: ContourTaskStatus;
  priority: ContourTaskPriority;
  deadlineUtc?: string | null;
  assigneeId?: string | null;
}

export interface ContourTheme {
  id: string;
  categoryId: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  order: number;
  completedStages: number;
  totalStages: number;
  stages: ContourStage[];
}

export interface ContourCategory {
  id: string;
  name: string;
  icon?: string | null;
  order: number;
  completedStages: number;
  totalStages: number;
  themes: ContourTheme[];
  totalThemes: number;
}

export interface ContourOverview {
  completedStages: number;
  totalStages: number;
  categories: ContourCategory[];
}

export const getContourOverview = () => apiRequest<ContourOverview>('/api/contour');

export interface ContourPage<T> { items: T[]; page: number; pageSize: number; total: number }
export interface ContourSearchResult {
  kind: 'category' | 'theme' | 'stage';
  id: string;
  name: string;
  description?: string | null;
  categoryId: string;
  categoryName: string;
  themeId?: string | null;
  themeName?: string | null;
}
export interface ContourAssignee { id: string; name: string }
export interface ContourComment { id: string; stageId: string; body: string; authorId: string; authorName?: string | null; createdAt: string }
export interface ContourTaskHistory { id: string; stageId: string; actorId: string; actorName?: string | null; action: string; changes: string; createdAt: string }

export const contourPageSize = 50;
const pagination = (page: number, pageSize = contourPageSize) => `page=${page}&pageSize=${pageSize}`;
export const getContourSummary = (signal?: AbortSignal) =>
  apiRequest<{ completedStages: number; totalStages: number }>('/api/contour/summary', { signal });
export const getContourCategories = (page = 1, signal?: AbortSignal) =>
  apiRequest<ContourPage<ContourCategory>>(`/api/contour/categories?${pagination(page)}`, { signal });
export const getContourCategory = (id: string, signal?: AbortSignal) =>
  apiRequest<ContourCategory>(`/api/contour/categories/${id}`, { signal });
export const getContourThemes = (categoryId: string, page = 1, signal?: AbortSignal) =>
  apiRequest<ContourPage<ContourTheme>>(`/api/contour/categories/${categoryId}/themes?${pagination(page)}`, { signal });
export const getContourTheme = (id: string, signal?: AbortSignal) =>
  apiRequest<ContourTheme>(`/api/contour/themes/${id}`, { signal });
export const getContourStages = (themeId: string, page = 1, signal?: AbortSignal, focusStageId?: string) =>
  apiRequest<ContourPage<ContourStage>>(`/api/contour/themes/${themeId}/stages?${pagination(page)}${focusStageId ? `&focusStageId=${encodeURIComponent(focusStageId)}` : ''}`, { signal });
export const searchContour = (query: string, page = 1, signal?: AbortSignal) =>
  apiRequest<ContourPage<ContourSearchResult>>(`/api/contour/search?${pagination(page, 40)}&query=${encodeURIComponent(query)}`, { signal });
export const getContourAssignees = () => apiRequest<ContourAssignee[]>('/api/contour/assignees');
export const getContourComments = (stageId: string, page = 1) =>
  apiRequest<ContourPage<ContourComment>>(`/api/contour/stages/${stageId}/comments?${pagination(page)}`);
export const createContourComment = (stageId: string, body: string) =>
  apiRequest<ContourComment>(`/api/contour/stages/${stageId}/comments`, { method: 'POST', body: { body } });
export const deleteContourComment = (stageId: string, commentId: string) =>
  apiRequest<void>(`/api/contour/stages/${stageId}/comments/${commentId}`, { method: 'DELETE' });
export const getContourTaskHistory = (stageId: string, page = 1) =>
  apiRequest<ContourPage<ContourTaskHistory>>(`/api/contour/history?stageId=${encodeURIComponent(stageId)}&${pagination(page)}`);
export const reorderContourCategoryPage = (ids: string[], expectedIds: string[], page = 1) =>
  apiRequest<void>('/api/contour/categories/reorder-page', { method: 'POST', body: { ids, expectedIds, page, pageSize: contourPageSize } });
export const reorderContourThemePage = (categoryId: string, ids: string[], expectedIds: string[], page = 1) =>
  apiRequest<void>(`/api/contour/categories/${categoryId}/themes/reorder-page`, { method: 'POST', body: { ids, expectedIds, page, pageSize: contourPageSize } });
export const reorderContourStagePage = (themeId: string, ids: string[], expectedIds: string[], page = 1) =>
  apiRequest<void>(`/api/contour/themes/${themeId}/stages/reorder-page`, { method: 'POST', body: { ids, expectedIds, page, pageSize: contourPageSize } });

export const createContourCategory = (body: { name: string; icon?: string | null }) =>
  apiRequest<ContourCategory>('/api/contour/categories', { method: 'POST', body });

export const updateContourCategory = (
  id: string,
  body: { name?: string; icon?: string | null; order?: number },
) => apiRequest<ContourCategory>(`/api/contour/categories/${id}`, { method: 'PATCH', body });

export const deleteContourCategory = (id: string) =>
  apiRequest<void>(`/api/contour/categories/${id}`, { method: 'DELETE' });

export const reorderContourCategories = (ids: string[], expectedIds?: string[]) =>
  apiRequest<void>('/api/contour/categories/reorder', { method: 'POST', body: { ids, expectedIds } });

export const createContourTheme = (body: {
  categoryId: string;
  name: string;
  description?: string | null;
  icon?: string | null;
}) => apiRequest<ContourTheme>('/api/contour/themes', { method: 'POST', body });

export const updateContourTheme = (
  id: string,
  body: { name?: string; description?: string | null; icon?: string | null; order?: number },
) => apiRequest<ContourTheme>(`/api/contour/themes/${id}`, { method: 'PATCH', body });

export const deleteContourTheme = (id: string) =>
  apiRequest<void>(`/api/contour/themes/${id}`, { method: 'DELETE' });

export const duplicateContourTheme = (id: string) =>
  apiRequest<ContourTheme>(`/api/contour/themes/${id}/duplicate`, { method: 'POST' });

export const reorderContourThemes = (categoryId: string, ids: string[], expectedIds?: string[]) =>
  apiRequest<void>(`/api/contour/categories/${categoryId}/themes/reorder`, {
    method: 'POST',
    body: { ids, expectedIds },
  });

export const createContourStage = (body: {
  themeId: string;
  name: string;
  description?: string | null;
} & Partial<ContourTaskMetadata>) => apiRequest<ContourStage>('/api/contour/stages', { method: 'POST', body });

export const updateContourStage = (
  id: string,
  body: { name?: string; description?: string | null; completed?: boolean; order?: number;
    clearDeadline?: boolean; clearAssignee?: boolean; expectedVersion?: number } & Partial<ContourTaskMetadata>,
) => apiRequest<ContourStage>(`/api/contour/stages/${id}`, { method: 'PATCH', body });

export const deleteContourStage = (id: string) =>
  apiRequest<void>(`/api/contour/stages/${id}`, { method: 'DELETE' });

export const reorderContourStages = (themeId: string, ids: string[], expectedIds?: string[]) =>
  apiRequest<void>(`/api/contour/themes/${themeId}/stages/reorder`, { method: 'POST', body: { ids, expectedIds } });

/** Математическое округление до целого процента (12.5 → 13), как требует ТЗ. */
export const contourPercent = (completed: number, total: number) =>
  total <= 0 ? 0 : Math.round((completed / total) * 100);

export const formatContourProgress = (completed: number, total: number) =>
  total <= 0 ? 'Нет этапов' : `${completed} из ${total} выполнено · ${contourPercent(completed, total)}%`;

/** «4 темы» с корректным склонением. */
export const formatThemeCount = (total: number) => {
  const remainder = total % 100;
  if (remainder >= 11 && remainder <= 14) return `${total} тем`;
  if (total % 10 === 1) return `${total} тема`;
  if (total % 10 >= 2 && total % 10 <= 4) return `${total} темы`;
  return `${total} тем`;
};
